import React from 'react';
import { Card, CardHeader, CardBody, Table, Button, Badge } from 'reactstrap';
import { useMutation } from '@apollo/client';
import { toast } from 'react-toastify';
import { RETRY_DELAYED_JOB, DESTROY_DELAYED_JOB } from '@api';
import config from '../../../config/appConfig';

const DELAYED_JOB_URL = `${(config.url.REACT_APP_BACKEND_BASE_URL || '').replace(/\/$/, '')}/delayed_job`;

export default function DelayedJobPanel({ summary, onDone }) {
  const [retry, { loading: rLoading }] = useMutation(RETRY_DELAYED_JOB, {
    onCompleted: (d) => { toast.success(`Retried ${d?.retryDelayedJob?.count || 0} jobs`); onDone && onDone(); },
    onError: (e) => toast.error(e.message),
  });
  const [destroy, { loading: dLoading }] = useMutation(DESTROY_DELAYED_JOB, {
    onCompleted: (d) => { toast.success(`Destroyed ${d?.destroyDelayedJob?.count || 0} jobs`); onDone && onDone(); },
    onError: (e) => toast.error(e.message),
  });
  const busy = rLoading || dLoading;

  const stat = (label, value, color) => (
    <div style={{ padding: '10px 16px', background: '#f0f2f5', borderRadius: 6, minWidth: 110, textAlign: 'center' }}>
      <div style={{ fontSize: 13, color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 600, color }}>{value}</div>
    </div>
  );

  return (
    <Card>
      <CardHeader style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <strong>Delayed Jobs</strong>
        <span style={{ marginLeft: 'auto' }}>
          <Button size='sm' color='primary' className='mr-1' disabled={busy || !summary?.failed} onClick={() => {
            if (!window.confirm(`Retry all ${summary?.failed} failed jobs?`)) return;
            retry({ variables: { all: true } });
          }}>Retry All Failed</Button>
          <Button size='sm' color='danger' disabled={busy || !summary?.failed} onClick={() => {
            if (!window.confirm(`Destroy all ${summary?.failed} failed jobs? This is permanent.`)) return;
            destroy({ variables: { onlyFailed: true } });
          }}>Clear Failed</Button>
          <a href={DELAYED_JOB_URL} target='_blank' rel='noopener noreferrer' style={{ marginLeft: 8, fontSize: 14 }}>Full UI ↗</a>
        </span>
      </CardHeader>
      <CardBody>
        <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
          {stat('Queued', summary?.queued ?? 0)}
          {stat('Running', summary?.running ?? 0, '#2a7')}
          {stat('Failed', summary?.failed ?? 0, summary?.failed ? '#c00' : undefined)}
          {stat('Scheduled', summary?.scheduled ?? 0)}
        </div>
        <div style={{ maxHeight: 240, overflowY: 'auto' }}>
          <Table size='sm' className='mb-0'>
            <thead>
              <tr><th>ID</th><th>Class</th><th>Attempts</th><th>Failed At</th><th>Error</th><th></th></tr>
            </thead>
            <tbody>
              {(summary?.recentFailed || []).length === 0 && (
                <tr><td colSpan={6} style={{ textAlign: 'center', color: '#888' }}>No failed jobs</td></tr>
              )}
              {(summary?.recentFailed || []).map((j) => (
                <tr key={j.id}>
                  <td>{j.id}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: 13 }}>{j.handlerClass}</td>
                  <td><Badge color='secondary'>{j.attempts}</Badge></td>
                  <td>{j.failedAt ? new Date(j.failedAt).toLocaleString() : '—'}</td>
                  <td style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13 }} title={j.lastError}>{j.lastError}</td>
                  <td>
                    <Button size='sm' color='link' disabled={busy} onClick={() => retry({ variables: { id: j.id } })}>retry</Button>
                    <Button size='sm' color='link' style={{ color: '#c00' }} disabled={busy} onClick={() => {
                      if (!window.confirm(`Destroy job ${j.id}?`)) return;
                      destroy({ variables: { id: j.id } });
                    }}>kill</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </CardBody>
    </Card>
  );
}
